import {CompanyEntity, Language, PersonType, toLanguageSafely, toPersonType} from "@ots-frontend/ots-data";
import {UseFormReturnType} from "@mantine/form";
import {useTranslation} from "react-i18next";
import {Badge, Button, Divider, Grid, Group, Modal, ModalProps, PasswordInput, Stack, Switch, Text, TextInput} from "@mantine/core";
import {useCallback} from "react";
import {useApp} from "../../../providers/RegionProvider/RegionProvider";
import {usePersonForm} from "./usePersonForm";
import {CompanySelect} from "../../select/entity/CompanySelect/CompanySelect";
import {PersonTypeSelect} from "../../select/entity/PersonTypeSelect/PersonTypeSelect";
import { LanguageSelect } from "../../select/entity/LanguageSelect/LanguageSelect";

export interface PersonFormProps {
  options?: PersonFormOptions;
  defaultValues?: PersonFormDefaultValues;
  onSubmit?: (values: PersonFormValues) => void;
  submitting?: boolean;
}

export interface PersonFormOptions {
  typeDisabled?: boolean
  types?: PersonType[]
  regionId?: string
}

export interface PersonFormValues {
  type: PersonType
  lastName: string;
  firstName: string;
  barcode?: string;
  company: CompanyEntity | string;
  username?: string
  password?: string
  passwordSet?: boolean
  passwordChanged?: boolean
  emailAddress?: string
  phoneNumber?: string
  preferredLanguage?: Language
  doNotUse?: boolean
}

export interface PersonFormDefaultValues extends Partial<PersonFormValues> {

}

export interface PersonFormRawValues {
  type?: string;
  lastName?: string
  firstName?: string
  barcode?: string;
  company?: string;
  username?: string
  password?: string;
  passwordSet?: boolean
  passwordChanged?: boolean
  emailAddress?: string
  phoneNumber?: string
  preferredLanguage?: string
  doNotUse?: boolean
}

export type PersonFormType = UseFormReturnType<PersonFormRawValues, (values: PersonFormRawValues) => PersonFormRawValues>

function SecretStatus({set}: { set?: boolean | null }) {
  return (
    <Badge variant="light">
      {set ? 'Set' : 'Not set'}
    </Badge>
  )
}

export const PersonForm = (props: PersonFormProps) => {
  const {
    options,
    defaultValues,
    onSubmit,
    submitting = false
  } = props;

  const region = useApp();

  const {t} = useTranslation(["common"]);

  const form = usePersonForm({
    defaultValues: defaultValues,
  });

  const onFormSubmit = useCallback(async (values: typeof form.values) => {
    if (onSubmit) {
      onSubmit({
        type: toPersonType(values.type!),
        lastName: values.lastName!,
        firstName: values.firstName!,
        barcode: values.barcode,
        company: values.company!,
        username: values.username,
        password: values.password || undefined,
        passwordSet: values.passwordSet,
        passwordChanged: Boolean(values.password),
        emailAddress: values.emailAddress,
        phoneNumber: values.phoneNumber,
        preferredLanguage: toLanguageSafely(values.preferredLanguage),
        doNotUse: values.doNotUse,
      });
    }
  }, [form, onSubmit]);

  return (
    <form onSubmit={form.onSubmit(onFormSubmit)}>
      <Grid grow gutter="xs">
        <Grid.Col span={12}>
          <PersonTypeSelect
            disabled={options?.typeDisabled}
            options={options?.types}
            label={t("common.entity.person.form.field.type", "Type")}
            placeholder={t<string>("common.entity.person.form.field.type.placeholder", "Type")}
            withAsterisk
            required
            {...form.getInputProps("type")} />
        </Grid.Col>
        <Grid.Col span={6}>
          <TextInput
            label={t("common.entity.person.form.field.firstname", "First Name")}
            placeholder={t<string>("common.entity.person.form.field.firstname.placeholder", "First Name")}
            withAsterisk
            required
            {...form.getInputProps("firstName")} />
        </Grid.Col>
        <Grid.Col span={6}>
          <TextInput
            label={t("common.entity.person.form.field.lastname", "Last Name")}
            placeholder={t<string>("common.entity.person.form.field.lastname.placeholder", "Last Name")}
            withAsterisk
            required
            {...form.getInputProps("lastName")} />
        </Grid.Col>
        <Grid.Col span={12}>
          <TextInput
            label={t("common.entity.person.form.field.barcode", "QR Code")}
            placeholder={t<string>("common.entity.person.form.field.barcode.placeholder", "QR Code")}
            {...form.getInputProps("barcode")} />
        </Grid.Col>
      </Grid>
      <Divider my="sm" variant="dotted"/>
      <Grid grow gutter="xs">
        <Grid.Col span={12}>
          <CompanySelect
            label={t("common.entity.person.form.field.company", "Company")}
            placeholder={t<string>("common.entity.person.form.field.company.placeholder", "Company")}
            selector={{
              regionId: region.regionId
            }}
            withAsterisk
            required
            clearable
            {...form.getInputProps("company")} />
        </Grid.Col>
      </Grid>
      <Divider my="sm" variant="dotted"/>
      <Grid grow gutter="xs">
        <Grid.Col span={6}>
          <TextInput
            label={t("common.entity.person.form.field.username", "Username")}
            placeholder={t<string>("common.entity.person.form.field.username.placeholder", "Username")}
            {...form.getInputProps("username")} />
        </Grid.Col>
        <Grid.Col span={6}>
          <Stack spacing="xs">
            <Group position="apart" align="center">
              <Text size="sm" fw={500}>
                {t("common.entity.person.form.field.password", "Password")}
              </Text>
              <SecretStatus set={form.values.passwordSet}/>
            </Group>
            <PasswordInput
              placeholder={form.values.passwordSet ? '•••••••• (set)' : t<string>("common.entity.person.form.field.password.placeholder", "Password")}
              autoComplete="new-password"
              {...form.getInputProps("password")} />
          </Stack>
        </Grid.Col>
      </Grid>
      <Divider my="sm" variant="dotted"/>
      <Grid>
        <Grid.Col span={6}>
          <TextInput
            label={t("common.entity.person.form.field.email", "Email")}
            placeholder={t<string>("common.entity.person.form.field.email.placeholder", "Email")}
            {...form.getInputProps("emailAddress")} />
        </Grid.Col>
        <Grid.Col span={6}>
          <TextInput
            label={t("common.entity.person.form.field.phonenumber", "Phone Number")}
            placeholder={t<string>("common.entity.person.form.field.phonenumber.placeholder", "Phone Number")}
            {...form.getInputProps("phoneNumber")} />
        </Grid.Col>
        <Grid.Col span={6}>
          <LanguageSelect
            label={t("common.entity.person.form.field.preferredlanguage", "Preferred Language")}
            placeholder={t<string>("common.entity.person.form.field.preferredlanguage.placeholder", "Preferred Language")}
            {...form.getInputProps("preferredLanguage")} />
        </Grid.Col>
      </Grid>
      <Divider my="sm" variant="dotted"/>
      <Switch
        label={t("common.entity.person.form.field.donotuse", "Do Not Use")}
        placeholder={t<string>("common.entity.person.form.field.donotuse", "Do Not Use")}
        {...form.getInputProps("doNotUse", {type: 'checkbox'})} />
      <Button type="submit" loading={submitting} fullWidth mt="xl">
        {t("common.action.submit", {ns: "common"})}
      </Button>
    </form>
  );
};

interface PersonFormModalProps extends PersonFormProps, Omit<ModalProps, 'onSubmit'> {

}

export const PersonFormModal = (props: PersonFormModalProps) => {
  const {
    defaultValues,
    onSubmit,
    submitting,
    ...rest
  } = props;

  return (
    <Modal {...rest}>
      <PersonForm defaultValues={defaultValues} onSubmit={onSubmit} submitting={submitting}/>
    </Modal>
  );
};
